/* eslint-disable no-empty */
import React, { useState, useRef, useEffect, useMemo } from "react";
import Input from "./Input";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const LeadContactForm = ({
  serviceOptions,
  income,
  setUser,
  questions,
  proxyUrl,
  redirectUrl,
  data_source = "VSL",
}) => {
  // Read query params directly from window.location (no react-router)
  const searchParams = useMemo(() => {
    if (typeof window === "undefined") {
      return new URLSearchParams("");
    }
    return new URLSearchParams(window.location.search || "");
  }, []);

  const [firstname, setFirstname] = useState(
    searchParams.get("firstname") || ""
  );
  const [lastname, setLastname] = useState(searchParams.get("lastname") || "");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [phone, setPhone] = useState(searchParams.get("phone") || "");
  const [zipcode, setZipcode] = useState(searchParams.get("zip") || "");
  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    email: false,
    phone: false,
    zipcode: false,
    options: false,
    general: "",
  });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const reCaptchaRef = useRef();

  // Params needed for backend
  const userpro_uuid = searchParams.get("pro_id");
  const pro_id = searchParams.get("pro_id");
  const qa_mode = searchParams.get("qa_mode");

  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  // normalize qa flag ONCE so we can reuse it
  const qaFlag =
    qa_mode === true ||
    qa_mode === "true" ||
    qa_mode === "on" ||
    qa_mode === "1";

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
    });
  }, []);

  // ✅ submit via proxy, skipping /public using raw + full /api path
  const submitAssasmentAnswers = async (uuid, user_id) => {
    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/api/assessments/submit-answers",
          qa_mode: qaFlag,
          raw: true,
          data: {
            pro_uuid: uuid,
            user_uuid: user_id,
            answers: questions?.map((q) => ({
              question_id: q?.id,
              answers: q?.selectedAnswers,
            })),
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      localStorage.removeItem("assesment");
      console.log(resp);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (errors.general !== "") {
      setErrors((prev) => ({ ...prev, general: "" }));
    }

    let recaptcha_token = null;
    if (reCaptchaRef.current?.executeAsync) {
      recaptcha_token = await reCaptchaRef.current.executeAsync();
    }

    setLoading(true);

    const payload = {
      userpro_uuid,
      recaptcha_token,
      firstname,
      lastname,
      email,
      phone: phone.split(/[ ()-]/).join(""),
      zipcode,
      options: serviceOptions,
      income: income,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/generate-lead",
          qa_mode: qaFlag,
          data: payload,
          params: {
            utm_source: "direct",
            utm_campaign: "free",
            utml_medium: "direct",
            tag: data_source || "VSL",
            pro_id: pro_id && pro_id,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const raw = resp?.data?.data || resp?.data || {};
      const user_id = raw.user_uuid || raw.user_id;
      const newUserproUuid = raw?.pro_uuid || "";
      const user = raw.user;

      setUser(user);
      submitAssasmentAnswers(newUserproUuid, user_id);
      setLoading(false);

      // 🔁 show "Redirecting..." then redirect
      if (redirectUrl) {
        setRedirecting(true);

        const delayMs = 1000;
        const currentEmail = email;

        setTimeout(() => {
          const url = new URL(redirectUrl, window.location.href);

          const srcParams = new URLSearchParams(window.location.search);
          srcParams.forEach((value, key) => {
            if (!url.searchParams.has(key)) {
              url.searchParams.set(key, value);
            }
          });

          if (currentEmail) url.searchParams.set("email", currentEmail);

          window.location.assign(url.toString());
        }, delayMs);
      }
    } catch (error) {
      setLoading(false);
      setErrors((prev) => ({
        ...prev,
        general: error?.response?.data?.message || error.message,
        phone: !error?.response?.data?.isPhoneValid || false,
      }));
    } finally {
      if (reCaptchaRef.current?.reset) {
        reCaptchaRef.current.reset();
      }
    }
  };

  // ✅ disable button until fields are filled & no phone/zip errors
  const isSubmitDisabled =
    !firstname.trim() ||
    !lastname.trim() ||
    !email.trim() ||
    !phone.trim() ||
    !zipcode.trim() ||
    errors.phone ||
    errors.zipcode ||
    loading ||
    redirecting;

  return (
    <div className="container" data-aos="fade-up">
      <form
        className="w-50 mt-2 px-3"
        style={{ maxWidth: "600px", margin: "0 auto" }}
      >
        <div className="mb-3">
          <label className="form-label">Your Full Name</label>
          <div className="row">
            <div className="col">
              <Input
                type="text"
                value={firstname}
                name="firstname"
                placeholder="First Name"
                showLabel={false}
                fn={setFirstname}
                labelClass="text-primary text-uppercase fw-semibold"
                inputClass="border border-2 bg-light"
                hasValidationError={errors.firstname}
              />
            </div>
            <div className="col">
              <Input
                type="text"
                value={lastname}
                name="lastname"
                placeholder="Last Name"
                showLabel={false}
                fn={setLastname}
                labelClass="text-primary text-uppercase fw-semibold"
                inputClass="border border-2 bg-light"
                hasValidationError={errors.lastname}
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <Input
            type="email"
            value={email}
            name="email"
            placeholder="Email Address"
            label={"Email Address"}
            fn={setEmail}
            labelClass="form-label"
            inputClass="border border-2 bg-light"
            hasValidationError={errors.email}
          />
        </div>

        <div className="mb-3">
          <Input
            type="text"
            value={phone}
            name="phone"
            placeholder="Phone Number"
            fn={setPhone}
            labelClass="form-label"
            inputClass="border border-2 bg-light"
            onLeave={() => {
              if (!errors.phone && phone.length > 1 && phone.length < 14) {
                setErrors({ ...errors, phone: true });
              } else if (errors.phone) {
                setErrors({ ...errors, phone: false });
              }
            }}
            hasValidationError={errors.phone}
            mask="(000) 000-0000"
          />
        </div>

        <div className="mb-3 w-50">
          <Input
            type="text"
            value={zipcode}
            name="zipcode"
            placeholder="Zip Code"
            label="Eg. 92112"
            fn={setZipcode}
            labelClass="form-label"
            inputClass="border border-2 bg-light"
            onLeave={() => {
              if (
                !errors.zipcode &&
                zipcode.length > 1 &&
                (zipcode.length < 5 || zipcode.length > 9)
              ) {
                setErrors({ ...errors, zipcode: true });
              } else if (errors.zipcode) {
                setErrors({ ...errors, zipcode: false });
              }
            }}
            hasValidationError={errors.zipcode}
            mask="00000000"
          />
        </div>

        {errors.general && errors.general !== "" && (
          <div className="row my-3">
            <div className="mb-3">
              <div className="alert alert-danger" role="alert">
                {errors.general}
              </div>
            </div>
          </div>
        )}

        {redirecting && (
          <div className="row my-2">
            <div className="mb-2">
              <div className="text-muted">Redirecting...</div>
            </div>
          </div>
        )}
      </form>

      <div className="d-flex justify-content-center mt-3">
        <button
          className="btn btn-primary py-2 px-5"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {loading ? (
            <div
              className="spinner-border spinner-border-sm"
              role="status"
            ></div>
          ) : (
            ""
          )}{" "}
          Create Account
        </button>
        {/* <ReCAPTCHA
          className="d-none"
          sitekey={RECAPTCHA_SITE_KEY}
          size="invisible"
          ref={reCaptchaRef}
        /> */}
      </div>
    </div>
  );
};

export default LeadContactForm;
